<script setup>
import { ref, getCurrentInstance } from 'vue'
import { notifyDialog, notifyDialogError } from '../composables/useDialogNotice'
import { DriverStore } from '../pinia/Driver.js'

const Driver = DriverStore()
const { proxy } = getCurrentInstance()

const mail = ref("" + Driver.user.EMAIL)
const password = ref('')
const deleteDialog = ref(false)
const deleteStep = ref(1)
const deleteConfirmText = ref('')
const deletePassword = ref('')

function save() {
    if(mail.value != Driver.user.EMAIL) {
        proxy.$http.post("", JSON.stringify({ serviceName: "account", methodName: "UpdateMail", parameters: [mail.value] })
        ).then(() => {
            Driver.user.EMAIL = mail.value
            notifyDialog({ type: 'success', message: 'Your account information has been saved.' })
        }).catch((err) => {
            notifyDialogError(err, 'Could not update email.')
        })
    }

    if(password.value.length > 0) {
        proxy.$http.post("", JSON.stringify({ serviceName: "account", methodName: "UpdatePass", parameters: [password.value] })
        ).then(() => {
            password.value = ''
            notifyDialog({ type: 'success', message: 'Your password has been updated.' })
        }).catch((err) => {
            notifyDialogError(err, 'Could not update password.')
        })
    }
}

function openDeleteDialog() {
    deleteStep.value = 1
    deleteConfirmText.value = ''
    deletePassword.value = ''
    deleteDialog.value = true
}

function nextDeleteStep() {
    if (deleteStep.value === 1) {
        deleteStep.value = 2
    } else if (deleteStep.value === 2) {
        if (deleteConfirmText.value.toLowerCase() === 'delete my account') {
            deleteStep.value = 3
        } else {
            notifyDialog({ type: 'error', message: 'Please type exactly "DELETE MY ACCOUNT"' })
        }
    } else if (deleteStep.value === 3) {
        deleteAccount()
    }
}

function deleteAccount() {
    proxy.$http.post("", JSON.stringify({
        serviceName: "account",
        methodName: "DeleteAccount",
        parameters: [deletePassword.value]
    })).then(response => {
        localStorage.clear()
        sessionStorage.clear()
        window.location.href = '/'
    }).catch((err) => {
        notifyDialogError(err, 'An error occurred')
    })
}

function cancelDelete() {
    deleteDialog.value = false
    deleteStep.value = 1
    deleteConfirmText.value = ''
    deletePassword.value = ''
}
</script>

<template><div class="settings-tab-page accountForm">
    <div class="settings-tab-header">
        <div class="header-icon">
            <v-icon size="28">mdi-account</v-icon>
        </div>
        <div class="header-content">
            <h3 class="page-title">Account</h3>
            <p class="page-subtitle">Manage your email and password</p>
        </div>
    </div>

    <v-form>
        <div class="section-list">
            <div class="section-row">
                <div class="section-body">
                    <div class="setting-item">
                        <div class="setting-info full-width">
                            <div class="setting-label">
                                <v-icon size="20" class="mr-2">mdi-email</v-icon>
                                Email Address
                            </div>
                            <div class="setting-description">
                                Your email address used for login and notifications
                            </div>
                            <v-text-field v-model="mail" placeholder="Email" type="email" variant="outlined" density="comfortable" hide-details class="setting-input"></v-text-field>
                        </div>
                    </div>

                    <v-divider class="my-4"></v-divider>

                    <div class="setting-item">
                        <div class="setting-info full-width">
                            <div class="setting-label">
                                <v-icon size="20" class="mr-2">mdi-lock</v-icon>
                                Change Password
                            </div>
                            <div class="setting-description">
                                Must contain at least 8 characters, one capital letter and one number
                            </div>
                            <v-text-field v-model="password" placeholder="New password" type="password" autocomplete="new-password" variant="outlined" density="comfortable" hide-details class="setting-input"></v-text-field>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <v-btn class="save-btn" variant="elevated" color="primary" size="large" @click="save" block>
            <v-icon class="mr-2">mdi-content-save</v-icon>
            Save Changes
        </v-btn>

        <div class="section-list danger-section">
            <div class="section-row danger">
                <div class="section-body">
                    <div class="setting-item">
                        <div class="setting-info full-width">
                            <div class="setting-label">
                                <v-icon size="20" class="mr-2">mdi-account-remove</v-icon>
                                Delete Account
                            </div>
                            <div class="setting-description danger">
                                Permanently delete your account and all associated data. This action cannot be undone.
                            </div>
                            <v-btn variant="outlined" color="error" @click="openDeleteDialog">
                                <v-icon class="mr-2">mdi-delete</v-icon>
                                Delete Account
                            </v-btn>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </v-form>

    <v-dialog v-model="deleteDialog" max-width="500" persistent>
        <v-card>
            <v-toolbar color="error" dark>
                <v-icon class="mr-2">mdi-alert</v-icon>
                <v-toolbar-title>Delete Account - Step {{ deleteStep }}/3</v-toolbar-title>
            </v-toolbar>

            <v-card-text class="pt-6">
                <div v-if="deleteStep === 1">
                    <h3 class="text-h6 mb-3">⚠️ Warning: Account Deletion</h3>
                    <p class="mb-2">You are about to permanently delete your account. This will:</p>
                    <ul class="mb-4">
                        <li>Delete all your drivers and personas</li>
                        <li>Remove all your cars and customizations</li>
                        <li>Delete your crew membership</li>
                        <li>Erase all your progress and statistics</li>
                        <li>Remove all your purchases (no refunds)</li>
                    </ul>
                    <p class="font-weight-bold error--text">This action is PERMANENT and CANNOT be undone!</p>
                </div>

                <div v-if="deleteStep === 2">
                    <h3 class="text-h6 mb-3">Confirm Account Deletion</h3>
                    <p class="mb-4">To confirm, please type exactly:</p>
                    <p class="font-weight-bold mb-4">DELETE MY ACCOUNT</p>
                    <v-text-field v-model="deleteConfirmText" placeholder="Type here..." variant="outlined" density="comfortable" autofocus @keyup.enter="nextDeleteStep"></v-text-field>
                </div>

                <div v-if="deleteStep === 3">
                    <h3 class="text-h6 mb-3">Final Confirmation</h3>
                    <p class="mb-4">Enter your password to permanently delete your account:</p>
                    <v-text-field v-model="deletePassword" placeholder="Your password" type="password" variant="outlined" density="comfortable" autofocus @keyup.enter="nextDeleteStep"></v-text-field>
                </div>
            </v-card-text>

            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn @click="cancelDelete">Cancel</v-btn>
                <v-btn color="error" variant="elevated" @click="nextDeleteStep" :disabled="deleteStep === 2 && deleteConfirmText.toLowerCase() !== 'delete my account'">
                    {{ deleteStep === 3 ? 'Delete Forever' : 'Continue' }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</div></template>

<style scoped>
.danger-section {
  margin-top:12px}
</style>
